import {FunctionsHttpError} from '@supabase/supabase-js';
import {Database} from '@tnc/shared';
import React, {ReactNode, createContext, useContext, useState} from 'react';

import {SupabaseInstance} from '@/lib/utils';

type ProductPositionAggregateT = Database['public']['Functions']['get_product_position_aggregates']['Returns'];
type SpeculatorPositionAggregateT = Database['public']['Functions']['get_speculator_position_aggregates']['Returns'];
type SingleProductPositionT = ProductPositionAggregateT[0];
type SingleSpeculatorPositionT = SpeculatorPositionAggregateT[0];

type ExchangeOrderContextT = {
  // Buy Order Values
  buyOrderProduct: SingleProductPositionT | null;
  selectBuyOrderProduct: (product: SingleProductPositionT) => void;
  submitBuyOrder: (params: {
    quantity: number;
    limitPrice: number;
    orderType: 'limit' | 'market';
    marketId: number;
  }) => Promise<{success: boolean; error?: string}>;

  // Sell Order Values
  sellOrderProduct: SingleSpeculatorPositionT | null;
  selectSellOrderProduct: (product: SingleSpeculatorPositionT) => void;
  submitSellOrder: (params: {
    quantity: number;
    limitPrice: number;
    orderType: 'limit' | 'market';
    marketId: number;
  }) => Promise<{success: boolean; error?: string}>;

  // Helpers
  resetForms: () => void;
};

const ExchangeOrderDataContext = createContext<ExchangeOrderContextT | undefined>(undefined);

export const useExchangeOrder = () => {
  const context = useContext(ExchangeOrderDataContext);
  if (!context) {
    throw new Error('useExchangeOrder must be used within an ExchangeOrderProvider');
  }
  return context;
};

type ExchangeOrderProviderPropsT = {
  children: ReactNode;
};

/**
 * Provider for the exchange order page
 * @param children - The children of the provider
 * @returns - The provider
 */
export const ExchangeOrderProvider: React.FC<ExchangeOrderProviderPropsT> = ({children}) => {
  // Hooks

  // State
  const [buyOrderProduct, setBuyOrderProduct] = useState<SingleProductPositionT | null>(null);
  const [sellOrderProduct, setSellOrderProduct] = useState<SingleSpeculatorPositionT | null>(null);

  // Refs

  /************************************************** */
  /****************** FUNCTIONALITY ******************* */
  /************************************************** */
  /**
   * Sets the product for the buy order
   * @param product - The product to set from the product selection modal
   */
  const selectBuyOrderProduct = (product: SingleProductPositionT) => {
    setBuyOrderProduct(product);
  };

  /**
   * Submits the buy order
   */
  const submitBuyOrder = async ({
    quantity,
    limitPrice,
    orderType,
    marketId,
  }: {
    quantity: number;
    limitPrice: number;
    orderType: 'limit' | 'market';
    marketId: number;
  }) => {
    if (!buyOrderProduct) return {success: false, error: 'No product selected'};
    const {data: user} = await SupabaseInstance.supabase.auth.getUser();
    const {data: session} = await SupabaseInstance.supabase.auth.getSession();

    if (!user?.user?.id) return {success: false, error: 'User not authenticated'};

    try {
      const {data, error} = await SupabaseInstance.supabase.functions.invoke('create-buy-order', {
        body: {
          buyOrder: {
            buy_type: orderType,
            brand_id: buyOrderProduct?.brand_id,
            market_id: marketId,
            product_id: buyOrderProduct?.product_id,
            quantity,
            limit_price: orderType === 'limit' ? limitPrice : buyOrderProduct.max_factored_price,
            payment_method: 'account',
            factored_price: buyOrderProduct.max_factored_price,
          },
          user_uid: user?.user?.id,
        },
        headers: {
          Authorization: `Bearer ${session?.session?.access_token}`,
        },
      });

      if (error && error instanceof FunctionsHttpError) {
        const {
          error: {message},
        } = await error.context.json();
        return {success: false, error: message ?? 'Failed to submit buy order'};
      }

      if (error) {
        return {
          success: false,
          error: error?.message ?? 'Failed to submit buy order',
        };
      }

      return {success: true, data};
    } catch (error) {
      console.error('Error submitting buy order', error);
      return {success: false, error: (error as Error).message};
    }
  };

  /**
   * Selects the sell order product
   */
  const selectSellOrderProduct = (product: SingleSpeculatorPositionT) => {
    setSellOrderProduct(product);
  };

  /**
   * Submits the sell order
   */
  const submitSellOrder = async ({
    quantity,
    limitPrice,
    orderType,
    marketId,
  }: {
    quantity: number;
    limitPrice: number;
    orderType: 'limit' | 'market';
    marketId: number;
  }): Promise<{success: boolean; error?: string}> => {
    if (!sellOrderProduct) return {success: false, error: 'No product selected'};

    const {data: session} = await SupabaseInstance.supabase.auth.getSession();
    const {data: user} = await SupabaseInstance.supabase.auth.getUser();

    if (!user?.user?.id) return {success: false, error: 'User not authenticated'};

    try {
      const {data, error} = await SupabaseInstance.supabase.functions.invoke('create-sell-order', {
        body: {
          sellOrder: {
            market_id: marketId,
            brand_id: sellOrderProduct.brand_id,
            product_id: sellOrderProduct.product_id,
            limit_price: orderType === 'limit' ? limitPrice : sellOrderProduct.max_factored_price,
            quantity,
            sell_type: orderType,
            payment_method: 'account',
            factored_price: sellOrderProduct.max_factored_price,
          },
          user_uid: user.user.id,
        },
        headers: {Authorization: `Bearer ${session?.session?.access_token}`},
      });

      if (error || (data && 'error' in data)) {
        const errorMessage = error?.message || (data as any)?.error?.message || 'Failed to submit sell order';
        return {success: false, error: errorMessage};
      }

      return {success: true, error};
    } catch (error) {
      console.error('Error submitting sell order', error);
      return {success: false, error: (error as Error).message};
    }
  };

  /************************************************** */
  /****************** HELPERS ******************* */
  /************************************************** */
  const resetForms = () => {
    setBuyOrderProduct(null);
    setSellOrderProduct(null);
  };

  /**
   * Value for the provider
   * @returns - The value for the provider
   */
  const value = {
    // Buy Order Values
    buyOrderProduct,
    selectBuyOrderProduct,
    submitBuyOrder,

    // Sell Order Values
    sellOrderProduct,
    selectSellOrderProduct,
    submitSellOrder,
    resetForms,
  };

  return <ExchangeOrderDataContext.Provider value={value}>{children}</ExchangeOrderDataContext.Provider>;
};

export default ExchangeOrderProvider;
